// OnboardingShell — sticky 3-step indicator + content slot.
//
// Per heshe Q17: the on-the-clock flow is "register → pair → first diary".
// Already paired users can skip with a banner (per W2 §10 #17 + #18).
//
// Steps:
//   1. 帳戶   — /register completed
//   2. 配對   — /pair or /pair/invite
//   3. 第一篇日記 — /onboarding/first-diary

import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { type ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

interface OnboardingShellProps {
  current: 1 | 2 | 3 | 4;
  /** When true, surfaces a "Skip to dashboard" button + link (paired users). */
  canSkip?: boolean;
  children: ReactNode;
}

/**
 * W36.1 (profile spec §3.4): a fourth step, 完善資料, between the account and
 * the pairing.
 *
 * **The order is the whole point.** A birthday collected *before*
 * `pair.confirmed` means the member-birthday fan-out runs on its own, and the
 * backfill CTA is only ever a safety net for people who already existed. Put
 * it after pairing and every single member has to go through the CTA instead —
 * and the fan-out's skip branch, which had no UI behind it for years, becomes
 * the normal path rather than the exception.
 */
const STEPS: ReadonlyArray<{ id: 1 | 2 | 3 | 4; label: string; path: string }> = [
  { id: 1, label: "帳戶", path: "/dashboard" },
  { id: 2, label: "完善資料", path: "/onboarding/profile" },
  { id: 3, label: "配對", path: "/pair" },
  { id: 4, label: "第一篇日記", path: "/onboarding/first-diary" },
];

export function OnboardingShell({ current, canSkip = false, children }: OnboardingShellProps) {
  return (
    <div className="min-h-[100dvh] w-full bg-paper flex flex-col" data-testid="onboarding-shell">
      <header
        className="safe-top sticky top-0 z-10 bg-paper-deep/95 backdrop-blur px-4 py-3 border-b border-hairline"
        data-testid="onboarding-step-indicator"
      >
        {/* W36.1: four steps do not fit the old three-step rail at 390px.
            Two changes, both needed — shrinking the gap alone still wrapped
            「第一篇日記」 onto three lines, and `whitespace-nowrap` alone
            overflowed the 420px rail. Labels drop to `text-meta` and the
            `w-7` badge to `w-6`, which buys back enough room for a 5-character
            label on one line at 390px; `overflow-x-auto` is the backstop for
            a longer translation rather than a silent clip. */}
        <ol className="flex items-center justify-center gap-2 max-w-[420px] mx-auto overflow-x-auto">
          {STEPS.map((step) => {
            const done = step.id < current;
            const active = step.id === current;
            return (
              <li key={step.id} className="flex items-center gap-1 shrink-0">
                <span
                  className={cn(
                    "flex items-center justify-center w-6 h-6 rounded-full text-micro border",
                    done
                      ? "bg-you border-you-strong text-you-strong"
                      : active
                        ? "bg-paper border-you-strong text-you-strong"
                        : "bg-paper border-hairline text-ink-3",
                  )}
                  data-testid={`step-${step.id}`}
                  data-status={done ? "done" : active ? "active" : "pending"}
                  aria-current={active ? "step" : undefined}
                >
                  {done ? <Check className="w-3 h-3" /> : step.id}
                </span>
                <span
                  className={cn(
                    "text-meta whitespace-nowrap",
                    active ? "text-ink-1 font-medium" : "text-ink-3",
                  )}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-[420px]">{children}</div>
      </main>
      {canSkip ? (
        <footer
          className="px-4 py-3 border-t border-hairline bg-paper-deep/60 text-center"
          data-testid="onboarding-skip"
        >
          <Link
            to="/dashboard"
            // `inline-flex min-h-11 items-center px-2 -mx-2` makes the tap area
            // 44px tall without moving the text: the underline and the label stay
            // on the original 18px line box, and the padding is symmetric so the
            // link still reads as centred in its footer row. As a bare text link
            // it measured 207.8×18 (W46.0) — the widest, flattest target in the
            // app, on the one screen a brand-new user is guaranteed to see.
            className="inline-flex min-h-11 items-center px-2 -mx-2 text-body-sm text-ink-2 underline"
            data-testid="onboarding-skip-link"
          >
            {/* Counted from STEPS, not written out. This string said "3 步"
                through the whole W36.1 profile sprint after the flow became
                four steps, which is the kind of stale copy nobody catches
                because it is grammatically fine. */}
            略過 {STEPS.length} 步流程, 直接到 dashboard
          </Link>
        </footer>
      ) : null}
    </div>
  );
}
